import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import { useVehiculesLoues } from "../locationsEnCours";
import { useModules } from "../modules";
import Layout from "../components/Layout";
import ImportVehicules from "../components/ImportVehicules";
import ParcVide from "../components/ParcVide";
import { peutAjouterVehicule } from "../roles";
import { SqueletteTableau } from "../components/Squelette";

export const CATEGORIES = [
  { valeur: "tracteur", label: "Tracteur" },
  { valeur: "porteur", label: "Porteur" },
  { valeur: "semi", label: "Semi" },
  { valeur: "leger", label: "Léger" },
  { valeur: "utilitaire", label: "Utilitaire" },
];

export function labelCategorie(valeur) {
  const c = CATEGORIES.find((x) => x.valeur === valeur);
  return c ? c.label : valeur;
}

const PAGE_SIZE = 20;

function Pagination({ page, nbPages, total, pageSize, onPage }) {
  if (nbPages <= 1) return null;
  const debut = (page - 1) * pageSize + 1;
  const fin = Math.min(page * pageSize, total);
  return (
    <div className="pagination">
      <button className="btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ← Précédent
      </button>
      <span className="pagination-info">
        {debut}–{fin} sur {total} · page {page}/{nbPages}
      </span>
      <button className="btn" disabled={page >= nbPages} onClick={() => onPage(page + 1)}>
        Suivant →
      </button>
    </div>
  );
}

export default function VehiculesListe({ user, onLogout }) {
  const navigate = useNavigate();
  const modules = useModules();
  const loues = useVehiculesLoues(user, modules);
  const [searchParams] = useSearchParams();
  const categorieUrl = searchParams.get("categorie");
  const filtreInitial =
    searchParams.get("etat") === "sortis"
      ? "sortis"
      : CATEGORIES.some((c) => c.valeur === categorieUrl)
      ? categorieUrl
      : "toutes";

  const [filtre, setFiltre] = useState(filtreInitial);
  const [rechercheInput, setRechercheInput] = useState("");
  const [recherche, setRecherche] = useState(""); // valeur debounced
  const [page, setPage] = useState(1);
  const [pageData, setPageData] = useState(null);
  const [chargement, setChargement] = useState(true);
  // `?importer=1` : le bouton « Importer un fichier » du tableau de bord d'un
  // parc vide mène ici, fenêtre d'import ouverte.
  const [modalImport, setModalImport] = useState(
    searchParams.get("importer") === "1" && peutAjouterVehicule(user)
  );
  const [erreur, setErreur] = useState("");
  const inputRef = useRef(null);

  // Debounce 250 ms sur la frappe
  useEffect(() => {
    const t = setTimeout(() => setRecherche(rechercheInput.trim()), 250);
    return () => clearTimeout(t);
  }, [rechercheInput]);

  const charger = useCallback(() => {
    setChargement(true);
    const params = { page, page_size: PAGE_SIZE };
    // « Sortis du parc » n'est pas une catégorie mais un état : le serveur
    // écarte les véhicules exclus par défaut, cette pastille les demande.
    if (filtre === "sortis") params.etat = "sortis";
    else if (filtre !== "toutes") params.categorie = filtre;
    if (recherche) params.recherche = recherche;
    api
      .get("/vehicules", { params })
      .then(({ data }) => setPageData(data))
      .catch(() => setErreur("Impossible de charger les véhicules."))
      .finally(() => setChargement(false));
  }, [page, filtre, recherche]);

  useEffect(() => {
    charger();
  }, [charger]);

  const changerFiltre = (cat) => {
    setFiltre(cat);
    setPage(1);
  };

  const changerRecherche = (val) => {
    setRechercheInput(val);
    setPage(1);
  };

  const effacerRecherche = () => {
    setRechercheInput("");
    setPage(1);
    inputRef.current?.focus();
  };

  const counts = pageData?.counts_by_categorie ?? {};
  // Les deux totaux viennent du serveur et ne bougent pas avec l'onglet : les
  // recalculer depuis `counts` donnerait le compte des SORTIS dès qu'on ouvre
  // leur onglet, sous le libellé « Toutes ».
  const totalToutes = pageData?.total_en_parc ?? 0;
  const totalSortis = pageData?.total_sortis ?? 0;
  const vueSortis = filtre === "sortis";

  // Groupement par catégorie des items de la page courante
  const items = pageData?.items ?? [];
  const groupes = {};
  items.forEach((v) => {
    if (!groupes[v.categorie]) groupes[v.categorie] = [];
    groupes[v.categorie].push(v);
  });
  const categoriesAffichees = CATEGORIES.filter((c) => groupes[c.valeur]);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <h2>Véhicules</h2>
        <div className="actions-groupe">
          {/* L'IMPORT ET LA CRÉATION SUIVENT LA MÊME RÈGLE QUE LE SERVEUR :
              `POST /vehicules` exige `exiger_admin`, tout comme l'import.
              Le commentaire disait déjà « comme la création à l'unité »… mais
              le bouton d'ajout, lui, n'était conditionné par rien.
              Deux conséquences, trouvées le 17/08 en écrivant le parcours
              navigateur de l'espace éditeur :
                - un TRAVAILLEUR voyait un bouton menant à un formulaire dont
                  l'enregistrement lui serait refusé (403) ;
                - pendant une CONSULTATION en lecture seule, l'éditeur le voyait
                  aussi — l'écran proposait donc d'écrire chez un client alors
                  que le serveur refuse toute écriture.
              Un bouton qui mène à un refus est un piège pour celui qui clique,
              et il fait douter de tout le reste de l'écran.
              Depuis le 05/10, le TRAVAILLEUR ajoute aussi des véhicules
              (décision de Yanis) : `peutAjouterVehicule`, double de
              `auth.ROLES_AJOUT_VEHICULE`. */}
          {peutAjouterVehicule(user) && (
            <>
              <button className="btn" onClick={() => setModalImport(true)}>
                Importer un fichier
              </button>
              <button
                className="btn-accent"
                onClick={() => navigate("/vehicules/nouveau")}
              >
                + Ajouter un véhicule
              </button>
            </>
          )}
        </div>
      </div>

      {modalImport && (
        <ImportVehicules
          url="/vehicules/import"
          titre="Importer des véhicules"
          sousTitre="Un fichier Excel, une ligne par véhicule. Rien n’est enregistré avant que vous confirmiez."
          onFini={charger}
          onFermer={() => setModalImport(false)}
        />
      )}

      {/* Barre de recherche */}
      <div className="barre-recherche">
        <input
          ref={inputRef}
          type="search"
          placeholder="Rechercher une immatriculation…"
          value={rechercheInput}
          onChange={(e) => changerRecherche(e.target.value)}
        />
        {rechercheInput && (
          <button className="btn" onClick={effacerRecherche}>
            Effacer
          </button>
        )}
      </div>

      {/* Chips catégorie, puis les véhicules sortis du parc.
          Les catégories sont masquées dans la vue « sortis » : elles y
          afficheraient la répartition des véhicules vendus, et un clic
          ramènerait au parc sans le dire. */}
      <div className="filtres">
        <button
          className={"chip" + (filtre === "toutes" ? " chip-actif" : "")}
          onClick={() => changerFiltre("toutes")}
        >
          Toutes ({totalToutes})
        </button>
        {!vueSortis &&
          CATEGORIES.map((c) => (
            <button
              key={c.valeur}
              className={"chip" + (filtre === c.valeur ? " chip-actif" : "")}
              onClick={() => changerFiltre(c.valeur)}
            >
              {c.label} ({counts[c.valeur] ?? 0})
            </button>
          ))}
        {/* Masquée tant qu'aucun véhicule n'est sorti : une pastille « (0) »
            invite à un clic qui ne montre rien. */}
        {(totalSortis > 0 || vueSortis) && (
          <button
            className={"chip chip-sortis" + (vueSortis ? " chip-actif" : "")}
            onClick={() => changerFiltre("sortis")}
          >
            Sortis du parc ({totalSortis})
          </button>
        )}
      </div>

      {/* Un squelette a la forme d'un tableau plutot qu'une ligne de texte :
          la place est deja prise, la page ne sursaute pas en se remplissant. */}
      {chargement && (
        <section className="groupe-categorie">
          <table className="tableau">
            <thead>
              <tr>
                <th>Immatriculation</th>
                <th>Marque</th>
                <th>Catégorie</th>
                <th>Hayon</th>
              </tr>
            </thead>
            <SqueletteTableau lignes={8} colonnes={4} />
          </table>
        </section>
      )}
      {erreur && <div className="erreur">{erreur}</div>}
      {/* Un parc VRAIMENT vide (ni véhicule en parc, ni sorti, aucune
          recherche) n'est pas une liste vide : c'est le premier jour d'un
          client, et l'écran doit dire quoi faire. */}
      {!chargement && !erreur && pageData?.total === 0 && !recherche
        && filtre === "toutes" && totalToutes === 0 && totalSortis === 0 && (
        <ParcVide user={user} avecBoutons={false} />
      )}
      {!chargement && !erreur && pageData?.total === 0
        && !(!recherche && filtre === "toutes" && totalToutes === 0 && totalSortis === 0) && (
        <p className="vide">
          {recherche
            ? `Aucun véhicule ne correspond à « ${recherche} ».`
            : vueSortis
            ? "Aucun véhicule sorti du parc."
            : "Aucun véhicule pour le moment."}
        </p>
      )}

      {!chargement && !erreur && categoriesAffichees.map((cat) => (
        <section key={cat.valeur} className="groupe-categorie">
          <h3>{cat.label}</h3>
          <table className="tableau">
            <thead>
              <tr>
                <th>Immatriculation</th>
                <th>Marque</th>
                <th>Catégorie</th>
                <th>Hayon</th>
              </tr>
            </thead>
            <tbody>
              {groupes[cat.valeur].map((v) => (
                <tr
                  key={v.id}
                  className="ligne-cliquable"
                  onClick={() => navigate(`/vehicules/${v.id}`)}
                >
                  <td>
                    <strong>{v.immatriculation}</strong>
                    {v.n_parc && <span className="sous-info"> · parc {v.n_parc}</span>}
                    {v.exclu && (
                      <span className="badge-exclu" style={{ marginLeft: "8px" }}>
                        Exclu
                      </span>
                    )}
                    {loues.has(v.id) && (
                      <span className="badge" style={{ marginLeft: "8px" }}>
                        En location
                      </span>
                    )}
                  </td>
                  <td>{v.marque || "—"}</td>
                  <td>{labelCategorie(v.categorie)}</td>
                  <td>{v.hayon ? <span className="badge">Hayon</span> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      {pageData && (
        <Pagination
          page={pageData.page}
          nbPages={pageData.nb_pages}
          total={pageData.total}
          pageSize={PAGE_SIZE}
          onPage={setPage}
        />
      )}
    </Layout>
  );
}
